import React from "react";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    arrayMove,
} from "@dnd-kit/sortable";
import { EditorField } from "./types";
import { SortableFieldItem } from "./sortableFieldItem";

interface SortableFieldListProps {
    fields: EditorField[];
    onFieldsChange: (fields: EditorField[]) => void;
    onUpdateField: (id: string, updates: Partial<EditorField>) => void;
    onDeleteField: (id: string) => void;
    onDuplicateField: (id: string) => void;
    validationErrors: Map<string, string>;
}

export const SortableFieldList = ({
    fields,
    onFieldsChange,
    onUpdateField,
    onDeleteField,
    onDuplicateField,
    validationErrors,
}: SortableFieldListProps) => {
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 5 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const oldIndex = fields.findIndex((f) => f.id === active.id);
            const newIndex = fields.findIndex((f) => f.id === over.id);
            onFieldsChange(arrayMove(fields, oldIndex, newIndex));
        }
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
        >
            <SortableContext
                items={fields.map((f) => f.id)}
                strategy={verticalListSortingStrategy}
            >
                {fields.map((field) => (
                    <SortableFieldItem
                        key={field.id}
                        field={field}
                        onUpdate={onUpdateField}
                        onDelete={onDeleteField}
                        onDuplicate={onDuplicateField}
                        validationError={validationErrors.get(field.id)}
                    />
                ))}
            </SortableContext>
        </DndContext>
    );
};
